import {PasswordInput, TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import _ from 'underscore';

import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {InputChangeEvent} from 'util/Types';

import {BenchlingAppDefinitionGraphQL as AppDefinition} from '../../../graphql-fragments/benchling-apps/__generated/BenchlingAppDefinitionGraphQL';
export type WebhookHeaderDomain = AppDefinition['webhookHeaders'][number];

export class WebhookHeader {
  key: string = '';
  value: string = '';
  submitAttempted: boolean = false;
}

export interface WebhookSetupSectionProps {
  onUpdateWebhookSetup: (
    newWebhookURL: string | null,
    newWebhookHeaders: Array<{key: string; value: string}>
  ) => Promise<void>;
  webhookSetupIsAccessible: boolean;
  webhookUrl: string | null;
  webhookHeaders: Array<WebhookHeaderDomain> | null;
}

export default function WebhookSetupSection({
  onUpdateWebhookSetup,
  webhookSetupIsAccessible,
  webhookUrl,
  webhookHeaders,
}: WebhookSetupSectionProps): JSX.Element {
  const webhookDocLink = (
    <a target="_blank" rel="noopener noreferrer" href="https://docs.benchling.com/docs/webhook-verification">
      webhook verification documentation
    </a>
  );
  const tooltip = <span>To learn more about verifying webhooks, visit our {webhookDocLink}.</span>;

  return (
    <InputWrapper
      id={_.uniqueId('benchlingAppDetail')}
      label="Webhook Setup"
      labelStyle={FieldLabel.STYLES.LABEL}
      help={tooltip}
      helpInteraction={FloatingInteraction.HOVER}
    >
      <Button
        text="Configure"
        buttonStyle={ButtonStyle.PRIMARY}
        disabled={!webhookSetupIsAccessible}
        size={ButtonSize.SMALL}
        onClick={(): void => {
          runModal<WebhookSetupModalProps>(WebhookSetupModal, {
            onUpdateWebhookSetup,
            initialWebhookUrl: webhookUrl,
            initialWebhookHeaders: webhookHeaders || [],
          });
        }}
      />
    </InputWrapper>
  );
}

interface WebhookSetupModalProps {
  onUpdateWebhookSetup: (
    newWebhookURL: string | null,
    newWebhookHeaders: Array<{key: string; value: string}>
  ) => Promise<void>;
  initialWebhookUrl: string | null;
  initialWebhookHeaders: Array<WebhookHeaderDomain>;
  onRequestClose: () => void;
}

export function WebhookSetupModal({
  onUpdateWebhookSetup,
  initialWebhookUrl,
  initialWebhookHeaders,
  onRequestClose,
}: WebhookSetupModalProps): JSX.Element | null {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(initialWebhookUrl);
  const [webhookHeaders, setWebhookHeaders] = useState(
    initialWebhookHeaders.map((h) => {
      const header = new WebhookHeader();
      header.key = h.key;
      header.value = h.value;
      return header;
    })
  );

  const submitHandler = (_: unknown): void => {
    setIsSubmitting(true);
    const newHeaders = [...webhookHeaders];
    for (let i = 0; i < webhookHeaders.length; i++) {
      newHeaders[i].submitAttempted = true;
    }
    setWebhookHeaders(newHeaders);

    for (const c of webhookHeaders) {
      if (c.key === '' || c.value === '') {
        notifications.error('Please fill out all fields prior to saving.');
        setIsSubmitting(false);
        return;
      }
    }

    const webhookHeadersPayload = webhookHeaders.map((h) => ({key: h.key, value: h.value}));
    onUpdateWebhookSetup(webhookUrl, webhookHeadersPayload)
      .then(() => {
        onRequestClose();
        notifications.success(<>Successfully updated app</>);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const MODAL_FOOTER: Array<ModalButton> = [
    {text: 'Cancel', onClick: onRequestClose, cancel: true, disabled: isSubmitting},
    {
      text: isSubmitting ? 'Saving...' : 'Save',
      onClick: submitHandler,
      submit: true,
      style: 'primary',
      disabled: isSubmitting,
    },
  ];

  const webhookDocLink = (
    <a target="_blank" rel="noopener noreferrer" href="https://docs.benchling.com/docs/webhook-verification">
      webhook verification documentation
    </a>
  );
  const tooltip = <span>To learn more about verifying webhooks, visit our {webhookDocLink}.</span>;

  return (
    <Modal onRequestClose={onRequestClose} buttons={MODAL_FOOTER} title="Webhook Setup">
      <InputWrapper
        id={_.uniqueId('webhookSetupModal')}
        label="Webhook URL"
        labelStyle={FieldLabel.STYLES.LABEL}
        help={tooltip}
        helpInteraction={FloatingInteraction.HOVER}
      >
        <div className={css(styles.webhookInput)}>
          <EditableTextField
            emptyValueAllowed={true}
            requestChange={(newWebhookUrl: string) => {
              setWebhookUrl(newWebhookUrl);
            }}
            type={TextFieldType.TEXT}
            value={webhookUrl || ''}
            isFullWidth={true}
          />
        </div>
      </InputWrapper>
      <InputWrapper
        id={_.uniqueId('webhookSetupModal')}
        label="Custom Webhook Headers"
        labelStyle={FieldLabel.STYLES.LABEL}
      >
        <div className={css(styles.headersContainer)}>
          {webhookHeaders.map((header: WebhookHeader, i) => (
            <WebhookHeaderRow
              key={i}
              header={header}
              i={i}
              webhookHeaders={webhookHeaders}
              setWebhookHeaders={setWebhookHeaders}
            />
          ))}
        </div>
      </InputWrapper>
      <Button
        text="+ Add Header"
        buttonStyle={ButtonStyle.TEXT}
        disabled={false}
        size={ButtonSize.SMALL}
        onClick={(): void => {
          setWebhookHeaders([...webhookHeaders, new WebhookHeader()]);
        }}
      />
    </Modal>
  );
}

interface WebhookHeaderRowProps {
  header: WebhookHeader;
  i: number;
  webhookHeaders: Array<WebhookHeader>;
  setWebhookHeaders: React.Dispatch<React.SetStateAction<Array<WebhookHeader>>>;
}

export function WebhookHeaderRow({
  header,
  i,
  webhookHeaders,
  setWebhookHeaders,
}: WebhookHeaderRowProps): JSX.Element | null {
  return (
    <div className={css(styles.headerRow)}>
      <div className={css(styles.halfWidth)}>
        <TextInput
          size="md"
          value={header.key}
          aria-label="Header key"
          error={header.submitAttempted && header.key === '' ? 'Field cannot be blank' : null}
          onChange={(e: InputChangeEvent) => {
            const newHeaders = [...webhookHeaders];
            newHeaders[i].key = e.target.value;
            setWebhookHeaders(newHeaders);
          }}
          placeholder="Header key"
          styles={{error: {display: 'none'}}}
        />
      </div>
      <div className={css(styles.halfWidth)}>
        <PasswordInput
          size="md"
          value={header.value}
          aria-label="Header value"
          error={header.submitAttempted && header.value === '' ? 'Field cannot be blank' : null}
          onChange={(e: InputChangeEvent) => {
            const newHeaders = [...webhookHeaders];
            newHeaders[i].value = e.target.value;
            setWebhookHeaders(newHeaders);
          }}
          placeholder="Header value"
        />
      </div>
      <Button
        buttonStyle={ButtonStyle.CIRCLE_ICON}
        size={ButtonSize.SMALL}
        icon="delete"
        onClick={() => {
          const newHeaders = webhookHeaders.slice(0, i).concat(webhookHeaders.slice(i + 1));
          setWebhookHeaders(newHeaders);
        }}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  headersContainer: {
    width: '100%',
    paddingTop: '10px',
  },
  headerRow: {
    paddingBottom: '5px',
    display: 'flex',
  },
  headerColumnTitle: {
    fontWeight: 'bold',
  },
  halfWidth: {
    width: '47%',
    display: 'inline-block',
    paddingRight: '10px',
  },
  webhookInput: {
    minWidth: '270px',
    marginBottom: '15px',
  },
});
